import type { Meeting, MeetingSchedule, MeetingStatus } from '@/modules/meetings/types';

export const meetingScheduleFromRow = (row: any): MeetingSchedule => ({
  id: row.id,
  teamId: row.team_id,
  meetingType: row.meeting_type,
  dtstart: row.dtstart,
  rrule: row.rrule,
  exdates: row.exdates ?? [],
  hostId: row.host_id ?? undefined,
  // `null` is Automatic — the roster follows the team, resolved at dispatch.
  // `?? []` here would freeze today's membership into every future meeting.
  invitees: row.invitees ?? null,
  prepEmailEnabled: row.prep_email_enabled ?? null,
  notificationTimingHours: row.notification_timing_hours ?? null,
  endedAt: row.ended_at ?? undefined,
});

export const meetingScheduleToRow = (schedule: Partial<MeetingSchedule>) => {
  const row: Record<string, unknown> = {};
  if (schedule.teamId !== undefined) row.team_id = schedule.teamId;
  if (schedule.meetingType !== undefined) row.meeting_type = schedule.meetingType;
  if (schedule.dtstart !== undefined) row.dtstart = schedule.dtstart;
  if (schedule.rrule !== undefined) row.rrule = schedule.rrule;
  if (schedule.exdates !== undefined) row.exdates = schedule.exdates;
  if (schedule.hostId !== undefined) row.host_id = schedule.hostId ?? null;
  if (schedule.invitees !== undefined) row.invitees = schedule.invitees;
  if (schedule.prepEmailEnabled !== undefined) row.prep_email_enabled = schedule.prepEmailEnabled;
  if (schedule.notificationTimingHours !== undefined) {
    row.notification_timing_hours = schedule.notificationTimingHours;
  }
  if (schedule.endedAt !== undefined) row.ended_at = schedule.endedAt ?? null;
  return row;
};

/** A `meetings` row as the store holds it. */
export const meetingFromRow = (r: any): Meeting => ({
    id: r.id,
    teamId: r.team_id ?? undefined,
    meetingScheduleId: r.meeting_schedule_id ?? undefined,
    occurrenceStart: r.occurrence_start ?? undefined,
    year: r.year ?? undefined,
    quarter: r.quarter ?? undefined,
    date: r.date,
    dataPeriodWeek: r.data_period_week,
    hostId: r.host_id ?? undefined,
    targetLengthMinutes: r.target_length_minutes,
    actualLengthMinutes: r.actual_length_minutes ?? undefined,
    averageScore: r.average_score != null ? Number(r.average_score) : undefined,
    reportMonthly: r.report_monthly,
    meetingType: r.meeting_type ?? 'weekly',
    sectionTargets: r.section_targets ?? {},
    sectionElapsedMap: r.section_elapsed_map ?? undefined,
    resolvedIssueIds: r.resolved_issue_ids ?? [],
    createdTaskIds: r.created_task_ids ?? [],
    unresolvedIssueIds: r.unresolved_issue_ids ?? [],
    notes: r.notes ?? undefined,
    issueNotes: r.issue_notes && Object.keys(r.issue_notes).length ? r.issue_notes : undefined,
    rockSnapshot: Array.isArray(r.rock_snapshot) && r.rock_snapshot.length
      ? r.rock_snapshot.map((s: any) => ({
          rockId: s.rock_id,
          title: s.title,
          status: s.status,
          ownerId: s.owner_id ?? undefined,
        }))
      : undefined,
    attendees: Array.isArray(r.attendees) ? r.attendees.map((a: any) => ({
      id: a.id, meetingId: a.meetingId ?? r.id, memberId: a.memberId,
      score: a.score ?? null,
      personalWin: a.personalWin ?? undefined,
      personalWinEmoji: a.personalWinEmoji ?? undefined,
      professionalWin: a.professionalWin ?? undefined,
      professionalWinEmoji: a.professionalWinEmoji ?? undefined,
    })) : [],
    fathomSummaries: Array.isArray(r.fathom_summaries)
      ? r.fathom_summaries.map((s: any) => ({
          url: s.url ?? '',
          summary: s.summary ?? undefined,
          candidateTasks: Array.isArray(s.candidate_todos)
            ? s.candidate_todos.map((c: any) => ({
                id: c.id,
                title: c.title,
                status: c.status ?? 'pending',
                decidedByUserId: c.decided_by_user_id ?? undefined,
                decidedAt: c.decided_at ?? undefined,
                acceptedTaskId: c.accepted_todo_id ?? undefined,
                matchedTaskId: c.matched_todo_id ?? undefined,
                matchedTaskTitle: c.matched_todo_title ?? undefined,
                assigneeName: c.assignee_name ?? undefined,
                assigneeEmail: c.assignee_email ?? undefined,
              }))
            : [],
          attachedAt: s.attached_at ?? '',
          attachedByUserId: s.attached_by_user_id ?? undefined,
          source: s.source ?? 'manual',
        }))
      : [],
    meetingRecap: r.meeting_recap ?? undefined,
    status: (r.status ?? 'complete') as MeetingStatus,
    sessionCount: r.session_count ?? 1,
    draftState: r.draft_state ?? undefined,
    scheduledAt: r.scheduled_at ?? undefined,
    invitees: r.invitees ?? [],
    notificationTimingHours: r.notification_timing_hours ?? undefined,
    prepEmailEnabled: r.prep_email_enabled ?? null,
    prepEmailSentAt: r.prep_email_sent_at ?? undefined,
    agenda: Array.isArray(r.agenda) ? r.agenda : undefined,
    sectionData: r.section_data && Object.keys(r.section_data).length ? r.section_data : undefined,
  });
